/**
 * COSOComplianceSection
 * 
 * Search result section for COSO Compliance (EX8)
 * Displays matching COSO notes and documents in tables
 * 
 * NOTE: This is a display-only component. Data is fetched by useGlobalSearch hook.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tooltip } from 'antd';
import { FilePdfOutlined, FileExcelOutlined, FileWordOutlined, FileUnknownOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import styled from 'styled-components';
import { ParsedCOSONote, ParsedCOSODocument, formatFileSize } from '../../../../services/cosoComplianceService';
import HighlightText from '../components/HighlightText';
import { htmlToPlainText } from '@/utils/exportUtils';

// ========================================
// Styled Components
// ========================================

const SectionContainer = styled.div`
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  background: #E0E7FF;
  padding: 12px 16px;
  border-radius: 8px 8px 0 0;
  border: 1px solid #C7D2FE;
  border-bottom: none;
`;

const SectionTitle = styled.h3`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #3730A3;
  margin: 0;
`;

const ContentContainer = styled.div`
  background: white;
  border: 1px solid #E5E7EB;
  overflow: hidden;
`;

const SubSectionHeader = styled.div`
  background: #F8FAFC;
  padding: 10px 16px;
  border-bottom: 1px solid #E5E7EB;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SubSectionTitle = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 13px;
  color: #374151;
`;

const SubSectionCount = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  color: #6B7280;
  background: #E5E7EB;
  padding: 2px 8px;
  border-radius: 10px;
`;

const TableWrapper = styled.div`
  overflow-x: auto;

  .ant-table {
    border-radius: 0;
  }

  .ant-table-thead > tr > th {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    font-size: 10px;
    color: #6B7280;
    padding: 6px 8px;
    border-bottom: 1px solid #E5E7EB;
    border-left: 1px solid #E5E7EB;
    white-space: nowrap;
    
    &:first-child {
      border-left: none;
    }
    
    /* Remove Ant Design's internal column separators */
    &::before {
      display: none !important;
    }
  }

  .ant-table-tbody > tr > td {
    font-family: Inter;
    font-size: 12px;
    color: #1F2937;
    padding: 12px 8px;
    border-bottom: 1px solid #E5E7EB;
    border-left: 1px solid #E5E7EB;
    vertical-align: middle;
    
    &:first-child {
      border-left: none;
    }
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }

  .ant-table-tbody > tr:hover > td {
    background: #F9FAFB;
  }
`;

// Chip Components
const PrincipleChip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 600;
  background: #EEF2FF;
  color: #3730A3;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #E0E7FF;
  }
`;

const ContentText = styled.div`
  font-size: 12px;
  color: #1F2937;
  line-height: 1.5;
  max-width: 500px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  cursor: pointer;
  
  &:hover {
    color: #2563EB;
  }
`;

const FileNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FileIcon = styled.span<{ $type: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  font-size: 14px;
  
  ${props => {
    switch (props.$type) {
      case 'pdf':
        return `
          background: #FEE2E2;
          color: #DC2626;
        `;
      case 'xls':
      case 'xlsx':
        return `
          background: #DCFCE7;
          color: #16A34A;
        `;
      case 'doc':
      case 'docx':
        return `
          background: #DBEAFE;
          color: #2563EB;
        `;
      default:
        return `
          background: #F3F4F6;
          color: #6B7280;
        `;
    }
  }}
`;

const FileName = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #1F2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 250px;
`;

const FileSizeText = styled.span`
  font-size: 12px;
  color: #6B7280;
`;

const DateText = styled.span`
  font-size: 12px;
  color: #6B7280;
`;

const EmptyCell = styled.span`
  color: #9CA3AF;
  font-size: 12px;
`;

// ========================================
// Types
// ========================================

export interface COSOComplianceResult {
  notes: ParsedCOSONote[];
  documents: ParsedCOSODocument[];
}

export interface COSOComplianceSectionProps {
  data: COSOComplianceResult;
  searchText: string;
}

// ========================================
// Helper Functions
// ========================================

const getFileIcon = (fileType: ParsedCOSODocument['fileType']) => {
  switch (fileType) {
    case 'pdf':
      return <FilePdfOutlined />;
    case 'xls':
    case 'xlsx':
      return <FileExcelOutlined />;
    case 'doc':
    case 'docx':
      return <FileWordOutlined />;
    default:
      return <FileUnknownOutlined />;
  }
};

const formatDate = (dateString: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit'
  });
};

// ========================================
// Component
// ========================================

const COSOComplianceSection: React.FC<COSOComplianceSectionProps> = ({
  data,
  searchText,
}) => {
  const navigate = useNavigate();

  // Navigation handlers
  const handleNavigateToPrinciple = (principleNumber: number) => {
    navigate(`/coso-compliance?principle=${principleNumber}`);
  };

  const handleNavigateToDocuments = () => {
    navigate('/coso-compliance?tab=documents');
  };

  // Notes columns
  const notesColumns: ColumnsType<ParsedCOSONote> = [
    {
      title: 'Principle #',
      dataIndex: 'principleNumber',
      key: 'principleNumber',
      width: 100,
      align: 'center',
      render: (num: number) => (
        <PrincipleChip onClick={() => handleNavigateToPrinciple(num)}>
          {num}
        </PrincipleChip>
      ),
    },
    {
      title: 'Content',
      dataIndex: 'content',
      key: 'content',
      render: (content: string, record) => (
        content ? (
          <ContentText onClick={() => handleNavigateToPrinciple(record.principleNumber)}>
            <HighlightText text={htmlToPlainText(content)} searchText={searchText} />
          </ContentText>
        ) : <EmptyCell>No content</EmptyCell>
      ),
    },
  ];

  // Documents columns
  const documentsColumns: ColumnsType<ParsedCOSODocument> = [
    {
      title: 'File Name',
      dataIndex: 'fileName',
      key: 'fileName',
      width: 350,
      render: (fileName: string, record) => (
        <FileNameContainer>
          <FileIcon $type={record.fileType}>
            {getFileIcon(record.fileType)}
          </FileIcon>
          <Tooltip title={fileName}>
            <FileName>
              <HighlightText text={fileName} searchText={searchText} />
            </FileName>
          </Tooltip>
        </FileNameContainer>
      ),
    },
    {
      title: 'Size',
      dataIndex: 'fileSize',
      key: 'fileSize',
      width: 100,
      render: (size: number) => (
        <FileSizeText>{formatFileSize(size)}</FileSizeText>
      ),
    },
    {
      title: 'Uploaded',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => (
        <DateText>{formatDate(date)}</DateText>
      ),
    },
  ];

  // Calculate total results
  const totalResults = data.notes.length + data.documents.length;

  // Don't render if no results
  if (totalResults === 0) {
    return null;
  }

  return (
    <SectionContainer>
      <SectionHeader>
        <SectionTitle>
          COSO Compliance ({totalResults} results)
        </SectionTitle>
      </SectionHeader>

      <ContentContainer>
        {/* Notes Sub-section */}
        {data.notes.length > 0 && (
          <>
            <SubSectionHeader>
              <SubSectionTitle>Compliance Notes</SubSectionTitle>
              <SubSectionCount>{data.notes.length}</SubSectionCount>
            </SubSectionHeader>
            <TableWrapper>
              <Table
                columns={notesColumns}
                dataSource={data.notes}
                rowKey="id"
                pagination={false}
                size="small"
              />
            </TableWrapper>
          </>
        )}

        {/* Documents Sub-section */}
        {data.documents.length > 0 && (
          <>
            <SubSectionHeader>
              <SubSectionTitle>Documents</SubSectionTitle>
              <SubSectionCount>{data.documents.length}</SubSectionCount>
            </SubSectionHeader>
            <TableWrapper>
              <Table
                columns={documentsColumns}
                dataSource={data.documents}
                rowKey="id"
                pagination={false}
                size="small"
                onRow={() => ({
                  onClick: () => handleNavigateToDocuments(),
                  style: { cursor: 'pointer' },
                })}
              />
            </TableWrapper>
          </>
        )}
      </ContentContainer>
    </SectionContainer>
  );
};

export default COSOComplianceSection;
